import {
  type TournamentCompetition,
  TournamentCompetitionConflict,
  TournamentCompetitionEvent,
  TournamentCompetitionStateSnapshot,
  TournamentEntry,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { setupModelRelations } from "@mixitone/oom";
import { deepEqual } from "@mixitone/util";
import tauri from "lib/tauri";
import { reduceTournamentState } from "../tournament-state/TournamentReducer";
import { tournamentEventStreamToken } from "../tournament-state/tournamentEventStreamToken";
import { projectMatchesFromState } from "../tournament-state/TournamentProjection";
import type {
  TournamentEventPayloadBase,
  TournamentEventType,
} from "../tournament-state/TournamentEventTypes";
import { type TournamentState, blankTournamentState } from "./types";

interface AppendEventOptions {
  clientId?: string;
  baseEventId?: string;
}

export class TournamentDataAdapter {
  /** One ephemeral id per JS session (tab load). Not persisted — reload yields a new stream key. */
  private static eventClientIdCache: string | undefined;

  /** Token for the event set applied by the most recent successful loadState. */
  lastLoadedEventToken: string | undefined;

  /**
   * Next `client_seq` per `(tournament_competition_id, client_id)` stream (next value to allocate).
   * Failed saves leave gaps — ordering uses `created_at` in TournamentReducer.sortEvents, not contiguous seq.
   */
  private static nextClientSeqByStream = new Map<string, number>();

  private defaultEventClientId() {
    if (TournamentDataAdapter.eventClientIdCache) {
      return TournamentDataAdapter.eventClientIdCache;
    }

    const unique =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    TournamentDataAdapter.eventClientIdCache = unique;
    return unique;
  }

  private streamKey(competitionId: string, clientId: string): string {
    return `${competitionId}::${clientId}`;
  }

  /** Monotonic seq for this tab/session only; synchronous before save (no overlapping max queries). */
  private allocateNextClientSeq(competitionId: string, clientId: string): number {
    const key = this.streamKey(competitionId, clientId);
    const next = TournamentDataAdapter.nextClientSeqByStream.get(key) ?? 1;
    TournamentDataAdapter.nextClientSeqByStream.set(key, next + 1);
    return next;
  }

  private isClientSeqUniqueViolation(error: unknown): boolean {
    const code = typeof error === "object" && error !== null ? (error as { code?: string }).code : undefined;
    const details =
      typeof error === "object" && error !== null ? (error as { details?: string }).details : undefined;
    const hint = typeof error === "object" && error !== null ? (error as { hint?: string }).hint : undefined;
    const message = error instanceof Error ? error.message : String(error);
    const combined = [message, details, hint].filter(Boolean).join(" ").toLowerCase();

    const mentionsClientSeqConstraint =
      combined.includes("uniq_tournament_competition_events_client_seq") ||
      combined.includes("tournament_competition_events_client_seq") ||
      (combined.includes("client_seq") && combined.includes("unique"));

    const isUniqueViolation =
      code === "23505" || combined.includes("duplicate key value violates unique constraint");

    return mentionsClientSeqConstraint || (isUniqueViolation && combined.includes("client_seq"));
  }

  private async resolveCompetitionIdentifiers(competition: TournamentCompetition) {
    let accountId = competition.account_id;
    let clubId = competition.club_id;

    if ((!accountId || !clubId) && competition.tournament_session_id) {
      const session = await TournamentSession.query().find(competition.tournament_session_id);
      if (session?.account_id && session?.club_id) {
        accountId = session.account_id;
        clubId = session.club_id;

        // Persist back to the competition so future writes don't need fallback.
        if (competition.account_id !== accountId || competition.club_id !== clubId) {
          competition.account_id = accountId;
          competition.club_id = clubId;
          await competition.save();
        }
      }
    }

    return { accountId, clubId };
  }

  private isLikelyTauriClient(): boolean {
    return tauri.enabled;
  }

  private debug(message: string, context?: Record<string, unknown>) {
    if (!this.isLikelyTauriClient()) return;
    if (context) {
      console.info(`[TournamentDataAdapter] ${message}`, context);
      return;
    }
    console.info(`[TournamentDataAdapter] ${message}`);
  }

  private normalizeEventPayload(event: TournamentCompetitionEvent) {
    const payload = event.payload;
    if (typeof payload !== "string") {
      return event;
    }

    try {
      event.payload = JSON.parse(payload) as Record<string, unknown>;
      this.debug("Parsed string payload for event", {
        competitionId: event.tournament_competition_id,
        eventId: event.id,
        eventType: event.event_type,
      });
    } catch (error) {
      this.debug("Failed to parse string payload for event", {
        competitionId: event.tournament_competition_id,
        eventId: event.id,
        eventType: event.event_type,
        payloadPreview: String(payload).slice(0, 160),
        error: error instanceof Error ? error.message : String(error),
      });
    }

    return event;
  }

  private isTerminalState(state: TournamentState): boolean {
    for (const round of state.rounds) {
      for (const match of round) {
        const hasTwoEntries = !!match[0]?.[0] && !!match[1]?.[0];
        if (!hasTwoEntries) continue;

        const hasWinner = !!match[0]?.[1] || !!match[1]?.[1];
        const isVoided = match[2]?.status === "voided";
        if (!hasWinner && !isVoided) {
          return false;
        }
      }
    }
    return true;
  }

  private async ensureConflicts(params: {
    competitionId: string;
    accountId: string;
    clubId: string;
    conflicts: Array<{
      id: string;
      conflict_type: string;
      event_ids: string[];
      payload: Record<string, unknown>;
      status: "open" | "resolved";
    }>;
  }) {
    const { competitionId, accountId, clubId, conflicts } = params;
    const openKeys = new Set(conflicts.map((conflict) => conflict.id));
    const persisted = await TournamentCompetitionConflict.query()
      .eq("tournament_competition_id", competitionId)
      .all();

    for (const existing of persisted) {
      if (existing.is_deleted || existing.status !== "open") continue;
      const key = existing.conflict_key || existing.id;
      if (!key || openKeys.has(key)) continue;
      console.log(`[TournamentConflict] ensureConflicts resolve stale key=${key}`);
      existing.status = "resolved";
      await existing.save();
    }

    for (const conflict of conflicts) {
      const existing = persisted.find((row) => (row.conflict_key || row.id) === conflict.id);
      if (existing) {
        const nextEventIds = conflict.event_ids || [];
        const hasChanges =
          existing.status !== conflict.status ||
          !deepEqual(existing.event_ids || [], nextEventIds) ||
          !deepEqual(existing.payload || {}, conflict.payload || {});

        if (hasChanges) {
          console.log(
            `[TournamentConflict] ensureConflicts update key=${conflict.id} type=${conflict.conflict_type}`,
          );
          existing.status = conflict.status;
          existing.event_ids = nextEventIds;
          existing.payload = conflict.payload;
          await existing.save();
        }
      } else {
        console.log(
          `[TournamentConflict] ensureConflicts insert key=${conflict.id} type=${conflict.conflict_type}`,
        );
        await new TournamentCompetitionConflict({
          account_id: accountId,
          club_id: clubId,
          tournament_competition_id: competitionId,
          conflict_key: conflict.id,
          conflict_type: conflict.conflict_type,
          event_ids: conflict.event_ids,
          payload: conflict.payload,
          status: conflict.status,
        }).save();
      }
    }
  }

  async appendEvent(params: {
    competition: TournamentCompetition;
    eventType: TournamentEventType;
    payload: TournamentEventPayloadBase;
    options?: AppendEventOptions;
  }): Promise<TournamentCompetitionEvent> {
    const { competition, eventType, payload, options } = params;
    if (!competition.id) {
      throw new Error("Competition is missing required identifiers");
    }
    const { accountId, clubId } = await this.resolveCompetitionIdentifiers(competition);
    if (!accountId || !clubId) {
      throw new Error("Competition is missing required identifiers");
    }

    const clientId = options?.clientId || this.defaultEventClientId();
    let lastError: unknown;
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const clientSeq = this.allocateNextClientSeq(competition.id, clientId);
      const event = new TournamentCompetitionEvent({
        account_id: accountId,
        club_id: clubId,
        tournament_competition_id: competition.id,
        event_type: eventType,
        payload,
        client_id: clientId,
        client_seq: clientSeq,
        base_event_id: options?.baseEventId,
      });

      try {
        await event.save();
        return event;
      } catch (error) {
        lastError = error;
        if (!this.isClientSeqUniqueViolation(error) || attempt === maxAttempts - 1) {
          throw error;
        }
        this.debug("Retrying appendEvent after client_seq uniqueness collision", {
          competitionId: competition.id,
          clientId,
          attempt: attempt + 1,
        });
        await new Promise((resolve) => {
          setTimeout(resolve, Math.min(80, 10 + attempt * 10));
        });
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  async saveSnapshot(params: {
    competition: TournamentCompetition;
    state: TournamentState;
    lastEventId?: string;
  }): Promise<void> {
    const { competition, state, lastEventId } = params;
    if (!competition.id) {
      return;
    }
    const { accountId, clubId } = await this.resolveCompetitionIdentifiers(competition);
    if (!accountId || !clubId) {
      return;
    }

    const existing = await TournamentCompetitionStateSnapshot.query()
      .eq("tournament_competition_id", competition.id)
      .orderBy("created_at", "desc")
      .first();

    if (existing) {
      const nextState = state as unknown as Record<string, unknown>;
      const hasChanges =
        !deepEqual(existing.state_blob || {}, nextState) || existing.last_event_id !== lastEventId;
      if (hasChanges) {
        existing.state_blob = nextState;
        existing.last_event_id = lastEventId;
        await existing.save();
      }
      return;
    }

    await new TournamentCompetitionStateSnapshot({
      account_id: accountId,
      club_id: clubId,
      tournament_competition_id: competition.id,
      state_blob: state as unknown as Record<string, unknown>,
      last_event_id: lastEventId,
    }).save();
  }

  async loadState(competition: TournamentCompetition): Promise<TournamentState> {
    if (!competition.id) {
      this.debug("Skipping loadState because competition.id is missing");
      return blankTournamentState();
    }
    const { accountId, clubId } = await this.resolveCompetitionIdentifiers(competition);

    const [players, registrations, entries, events, persistedConflicts] = await Promise.all([
      TournamentPlayer.query().eq("tournament_session_id", competition.tournament_session_id).all(),
      TournamentRegistration.query().eq("tournament_competition_id", competition.id).all(),
      TournamentEntry.query().eq("tournament_competition_id", competition.id).orderBy("seed").all(),
      TournamentCompetitionEvent.query()
        .eq("tournament_competition_id", competition.id)
        .orderBy("created_at")
        .all(),
      TournamentCompetitionConflict.query().eq("tournament_competition_id", competition.id).all(),
    ]);

    setupModelRelations({
      players,
      registrations,
      entries,
      competition,
    });

    const normalizedEvents = events.map((event) => this.normalizeEventPayload(event));
    const activeEvents = normalizedEvents.filter((event) => !event.is_deleted);
    const validEntries = entries.filter((entry) => entry.validRegistrations);
    this.debug("Loaded tournament competition state inputs", {
      competitionId: competition.id,
      competitionAccountId: accountId || null,
      competitionClubId: clubId || null,
      players: players.length,
      registrations: registrations.length,
      entries: entries.length,
      validEntries: validEntries.length,
      events: events.length,
      activeEvents: activeEvents.length,
      persistedConflicts: persistedConflicts.length,
      eventPayloadTypes: activeEvents.map((event) => typeof event.payload),
    });

    if (entries.length > 0 && validEntries.length === 0) {
      this.debug("No valid entries after relation setup", {
        competitionId: competition.id,
        entryIds: entries.map((entry) => entry.id),
        registrationIds: registrations.map((registration) => registration.id),
      });
    }

    const reduced = reduceTournamentState({
      competition,
      entries: validEntries,
      events: activeEvents,
      persistedConflicts: persistedConflicts.filter((conflict) => !conflict.is_deleted),
    });

    const winnerSelected = activeEvents.filter((event) => event.event_type === "winner_selected");
    console.log(
      `[TournamentConflict] loadState competition=${competition.id} events=${activeEvents.length} winner_selected=${winnerSelected.length} detectedConflicts=${reduced.conflicts.length} persistedConflicts=${persistedConflicts.length}`,
    );
    for (const event of winnerSelected) {
      const slot = event.payload?.slot as { slot_id?: string } | undefined;
      console.log(
        `[TournamentConflict] loadState winner_selected id=${event.id} slot=${slot?.slot_id ?? "?"} winner=${String(event.payload?.winner_id ?? "")}`,
      );
    }

    if (accountId && clubId) {
      await this.ensureConflicts({
        competitionId: competition.id,
        accountId,
        clubId,
        conflicts: reduced.conflicts,
      });
    } else if (reduced.conflicts.length > 0) {
      this.debug("Skipping conflict persistence due missing identifiers", {
        competitionId: competition.id,
        conflicts: reduced.conflicts.length,
      });
    }

    const latestEventId = events.at(-1)?.id;
    await this.saveSnapshot({ competition, state: reduced.state, lastEventId: latestEventId });

    const nextWinners = reduced.state.winners || [];
    const currentWinners = competition.format_data?.winners || [];
    if (!deepEqual(currentWinners, nextWinners)) {
      competition.format_data = {
        ...(competition.format_data || {}),
        winners: nextWinners,
      };
      await competition.save();
    }

    if (competition.canComplete && nextWinners.length > 0 && this.isTerminalState(reduced.state)) {
      await competition.complete();
    }

    this.lastLoadedEventToken = tournamentEventStreamToken(events);
    return reduced.state;
  }

  async loadProjectedMatches(competition: TournamentCompetition) {
    if (!competition.id) {
      this.debug("Skipping projection because competition.id is missing");
      return [];
    }
    if (!competition.account_id || !competition.club_id) {
      this.debug("Projecting matches with missing identifiers on competition", {
        competitionId: competition.id,
        accountId: competition.account_id || null,
        clubId: competition.club_id || null,
      });
    }
    const state = await this.loadState(competition);
    const projected = projectMatchesFromState({
      state,
      competitionId: competition.id,
      accountId: competition.account_id,
      clubId: competition.club_id,
    });
    this.debug("Projected matches from state", {
      competitionId: competition.id,
      projectedMatches: projected.length,
      rounds: state.rounds.length,
      winners: state.winners?.length || 0,
    });
    return projected;
  }
}
